/** Feature flag that gates every Prism retrieval view (#3261). */
export const FILTH_SEARCH_VISUALIZATION_FEATURE_ID = "filth_search_visualization"

/** The five Prism retrieval views, in the order the pickers list them. */
export const PRISM_VIEWS = ["levels", "focus", "access", "depth", "flow"] as const

export type PrismView = (typeof PRISM_VIEWS)[number]

/** The per-user choice that defers to the company default. */
export const PRISM_VIEW_COMPANY = "company"

/** A user's stored preference: a concrete view, or "use the company default". */
export type PrismViewPreference = typeof PRISM_VIEW_COMPANY | PrismView

/** Last-resort view when neither the user nor the company picked a valid one. */
export const DEFAULT_PRISM_VIEW: PrismView = "levels"

/** Shape of `filth_search_visualization.config` on `/config` features. */
export type FilthSearchVisualizationFeatureConfig = {
  default_view?: string
}

/**
 * A cited FILTH source of the current answer, as Prism may name it (#3261).
 * Built only from the turn's own citations, never from search results: a
 * document the answer did not cite is never named or typed anywhere in Prism.
 */
export type PrismSource = {
  /** The citation number the answer shows for this source (1-based). */
  n: number
  title: string
  /** The library (knowledge space) the cited source lives in, when known. */
  spaceLabel?: string
}

/** A turn's citations split for Prism (#3261), see `getPrismCitations`. */
export type PrismCitations = {
  /** Cited FILTH documents, which Prism names. */
  sources: PrismSource[]
  /** The answer's 0-based index of each other distinct cited source, never named. */
  otherCitationIndexes: number[]
}

/** One Guiding beat (`--dur-guiding`): the least time between two steps. */
export const PRISM_GUIDING_MS = 220
/** Focus (`--dur-brand`): the Prism focus pull and a region settling. */
export const PRISM_FOCUS_MS = 600

/**
 * Pacing of the live sequences, in ms from mount. Presentation only: a step
 * never passes what `details.filth_search` has reported (see
 * `getPrismStepCap`), and the steps it unlocks follow one Guiding beat apart
 * (`usePrismSequence`). Each step change itself moves on Guiding
 * (`--dur-guiding`); the focus pull on the cited sources is Focus
 * (`--dur-brand`), both in `prism-retrieval.css`.
 */
export const PRISM_FLOW_STEP_MS = [400, 820, 1240, 1660] as const
export const PRISM_ACCESS_STEP_MS = [500, 1000, 1500] as const
/**
 * How the Access panel's two reserved regions settle when the turn ends
 * (#3261): each closes in two halves one Guiding beat apart
 * (`getPrismSettleParts`), the tiles two beats after the spaces, so no two
 * movements peak together and the answer below never moves faster than about
 * 6 px per frame on the brand curve.
 */
export const PRISM_ACCESS_SETTLE = {
  spaces: { split: true, delayMs: 0 },
  tiles: { split: true, delayMs: 2 * PRISM_GUIDING_MS },
} as const
export const PRISM_DEPTH_STEP_MS = [200, 760, 1320] as const

/** Abstract, textless placeholders drawn behind the cited sources. */
export const PRISM_FOCUS_PLACEHOLDERS = 10
/**
 * Evidence slots the focus panel pulls into focus at grounding, one per
 * reported evidence item. Cited sources are named in these slots; a source
 * beyond them takes a slot of its own at the end of the row.
 */
export const PRISM_FOCUS_SLOTS = 4
export const PRISM_DEPTH_PLACEHOLDERS_MAX = 12
export const PRISM_DEPTH_PLACEHOLDERS_PENDING = 8
/** Total evidence tiles (named sources + abstract tiles) in the access tray. */
export const PRISM_ACCESS_TILES_MAX = 12
/** Stagger between tiles guiding in, in ms. */
export const PRISM_TILE_STAGGER_MS = 60
/**
 * The evidence count a live state line reserves room for before FILTH has
 * reported one: two digits, in the plural.
 */
export const PRISM_RESERVE_COUNT = 88

/** One step of a Prism step track (flow row, access panel). */
export type PrismStep = {
  key: string
  label: string
  /**
   * The widest label the step can change to, when it changes its label as it
   * completes. Its width is reserved from mount, so nothing after it moves.
   */
  reserve?: string
}

/**
 * Where the focus panel's textless placeholders sit, as [left %, top %,
 * width px]. Decoration only: they carry no text and stand for no document.
 */
export const PRISM_FOCUS_PLACEHOLDER_LAYOUT: readonly (readonly [number, number, number])[] = [
  [14, 62, 88],
  [34, 76, 112],
  [54, 58, 72],
  [74, 74, 96],
  [90, 56, 64],
  [24, 40, 104],
  [62, 34, 80],
  [84, 36, 92],
  [44, 40, 76],
  [70, 52, 100],
]

/** Soft background lenses behind the focus field, as [left %, top %, size px]. */
export const PRISM_FOCUS_LENSES: readonly (readonly [number, number, number])[] = [
  [6, -10, 60],
  [28, 20, 83],
  [52, 0, 106],
  [72, 30, 69],
  [88, -6, 92],
]

/** Widths (px) cycled by the access tray's textless evidence tiles. */
export const PRISM_TILE_WIDTHS = [44, 64, 52, 72, 40, 58] as const
